import React, { useState, useEffect } from 'react';
import {
  Package, Wrench, TrendingUp, ShoppingCart, Gem,
  Plus, Edit2, Save, X, DollarSign, Calendar, Check, AlertCircle
} from 'lucide-react';
import api, { API_URL, getServerUrl } from '../../services/api';

const GestionCouts = () => {
  const [activeTab, setActiveTab] = useState('packaging');
  const [loading, setLoading] = useState(false);
  
  // États pour chaque module
  const [packaging, setPackaging] = useState([]);
  const [coutsFabrication, setCoutsFabrication] = useState([]);
  const [historiquePrixOr, setHistoriquePrixOr] = useState([]);
  const [achatsMatieres, setAchatsMatieres] = useState([]);
  const [achatsPierres, setAchatsPierres] = useState([]);
  const [statistiquesPierres, setStatistiquesPierres] = useState(null);
  
  // États pour l'édition
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({});

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      switch (activeTab) {
        case 'packaging':
          const packRes = await api.get(`${API_URL}/gestion-couts/packaging`, config);
          setPackaging(packRes.data);
          break;
        case 'couts-fabrication':
          const coutsRes = await api.get(`${API_URL}/gestion-couts/couts-fabrication`, config);
          setCoutsFabrication(coutsRes.data);
          break;
        case 'prix-or':
          const orRes = await api.get(`${API_URL}/gestion-couts/historique-prix-or`, config);
          setHistoriquePrixOr(orRes.data);
          break;
        case 'matieres':
          const matRes = await api.get(`${API_URL}/gestion-couts/achats-matieres`, config);
          setAchatsMatieres(matRes.data);
          break;
        case 'pierres':
          const pierrRes = await api.get(`${API_URL}/gestion-couts/achats-pierres`, config);
          setAchatsPierres(pierrRes.data);
          const statsRes = await api.get(`${API_URL}/gestion-couts/achats-pierres/statistiques`, config);
          setStatistiquesPierres(statsRes.data);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditForm(item);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      let endpoint = '';
      switch (activeTab) {
        case 'packaging':
          endpoint = `${API_URL}/gestion-couts/packaging/${editingId}`;
          break;
        case 'couts-fabrication':
          endpoint = `${API_URL}/gestion-couts/couts-fabrication/${editingId}`;
          break;
        case 'prix-or':
          endpoint = `${API_URL}/gestion-couts/historique-prix-or/${editingId}`;
          break;
        case 'matieres':
          endpoint = `${API_URL}/gestion-couts/achats-matieres/${editingId}`;
          break;
        case 'pierres':
          endpoint = `${API_URL}/gestion-couts/achats-pierres/${editingId}`;
          break;
        default:
          return;
      }

      await api.put(endpoint, editForm, config);
      setEditingId(null);
      setEditForm({});
      loadData();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleAdd = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      let endpoint = '';
      switch (activeTab) {
        case 'packaging':
          endpoint = `${API_URL}/gestion-couts/packaging`;
          break;
        case 'couts-fabrication':
          endpoint = `${API_URL}/gestion-couts/couts-fabrication`;
          break;
        case 'prix-or':
          endpoint = `${API_URL}/gestion-couts/historique-prix-or`;
          break;
        case 'matieres':
          endpoint = `${API_URL}/gestion-couts/achats-matieres`;
          break;
        case 'pierres':
          endpoint = `${API_URL}/gestion-couts/achats-pierres`;
          break;
        default:
          return;
      }

      await api.post(endpoint, addForm, config);
      setShowAddForm(false);
      setAddForm({});
      loadData();
    } catch (error) {
      console.error('Erreur ajout:', error);
      alert('Erreur lors de l\'ajout');
    }
  };

  const handleAddPaiement = async (pierreId, montant, datePaiement) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await api.post(
        `${API_URL}/gestion-couts/achats-pierres/${pierreId}/paiement`,
        { montant, date_paiement: datePaiement, mode_paiement: 'especes' },
        config
      );
      loadData();
    } catch (error) {
      console.error('Erreur ajout paiement:', error);
      alert('Erreur lors de l\'ajout du paiement');
    }
  };

  // Rendu du tableau Packaging
  const renderPackaging = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800">📦 Coûts de Packaging</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} />
          Ajouter
        </button>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-gray-200">
        {packaging.map(item => (
          <div key={item.id} className="p-4 space-y-1">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-900">{item.type}</span>
              <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800">
                <Edit2 size={18} />
              </button>
            </div>
            <div className="text-sm text-gray-600">HT: {item.prix_ht} DH · TTC: {item.prix_ttc} DH · Maj: {item.majoration} DH</div>
          </div>
        ))}
      </div>

      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix HT (DH)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix TTC (DH)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Majoration (DH)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {packaging.map(item => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  {item.type}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingId === item.id ? (
                    <input
                      type="text" inputMode="decimal" onFocus={e => e.target.select()}
                      value={editForm.prix_ht || ''}
                      onChange={(e) => setEditForm({...editForm, prix_ht: e.target.value})}
                      className="w-24 px-2 py-1 border rounded"
                    />
                  ) : (
                    <span>{item.prix_ht} DH</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingId === item.id ? (
                    <input
                      type="text" inputMode="decimal" onFocus={e => e.target.select()}
                      value={editForm.prix_ttc || ''}
                      onChange={(e) => setEditForm({...editForm, prix_ttc: e.target.value})}
                      className="w-24 px-2 py-1 border rounded"
                    />
                  ) : (
                    <span>{item.prix_ttc} DH</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingId === item.id ? (
                    <input
                      type="text" inputMode="decimal" onFocus={e => e.target.select()}
                      value={editForm.majoration || ''}
                      onChange={(e) => setEditForm({...editForm, majoration: e.target.value})}
                      className="w-24 px-2 py-1 border rounded"
                    />
                  ) : (
                    <span>{item.majoration} DH</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingId === item.id ? (
                    <div className="flex gap-2">
                      <button onClick={handleSave} className="text-green-600 hover:text-green-800">
                        <Save size={18} />
                      </button>
                      <button onClick={() => setEditingId(null)} className="text-red-600 hover:text-red-800">
                        <X size={18} />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800">
                      <Edit2 size={18} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Rendu du tableau Coûts de Fabrication
  const renderCoutsFabrication = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800">🔧 Coûts de Fabrication</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} />
          Ajouter
        </button>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-gray-200">
        {coutsFabrication.map(item => (
          <div key={item.id} className="p-4 space-y-1">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-900">{item.nom}</span>
              <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800">
                <Edit2 size={18} />
              </button>
            </div>
            <div className="text-sm text-gray-600">{item.cout} DH · <span className={`px-2 py-0.5 rounded text-xs font-medium ${item.type_cout === 'MINUTE' ? 'bg-blue-100 text-blue-800' : item.type_cout === 'G' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'}`}>{item.type_cout}</span></div>
          </div>
        ))}
      </div>

      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Coût</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {coutsFabrication.map(item => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  {editingId === item.id ? (
                    <input
                      type="text"
                      value={editForm.nom || ''}
                      onChange={(e) => setEditForm({...editForm, nom: e.target.value})}
                      className="w-full px-2 py-1 border rounded"
                    />
                  ) : (
                    item.nom
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingId === item.id ? (
                    <input
                      type="text" inputMode="decimal" onFocus={e => e.target.select()}
                      step="0.01"
                      value={editForm.cout || ''}
                      onChange={(e) => setEditForm({...editForm, cout: e.target.value})}
                      className="w-24 px-2 py-1 border rounded"
                    />
                  ) : (
                    <span>{item.cout} DH</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingId === item.id ? (
                    <select
                      value={editForm.type_cout || ''}
                      onChange={(e) => setEditForm({...editForm, type_cout: e.target.value})}
                      className="px-2 py-1 border rounded"
                    >
                      <option value="MINUTE">MINUTE</option>
                      <option value="G">G (gramme)</option>
                      <option value="UNITAIRE">UNITAIRE</option>
                    </select>
                  ) : (
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item.type_cout === 'MINUTE' ? 'bg-blue-100 text-blue-800' :
                      item.type_cout === 'G' ? 'bg-green-100 text-green-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {item.type_cout}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingId === item.id ? (
                    <div className="flex gap-2">
                      <button onClick={handleSave} className="text-green-600 hover:text-green-800">
                        <Save size={18} />
                      </button>
                      <button onClick={() => setEditingId(null)} className="text-red-600 hover:text-red-800">
                        <X size={18} />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800">
                      <Edit2 size={18} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Rendu du tableau Historique Prix Or
  const renderHistoriquePrixOr = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800">📈 Historique Prix de l'Or</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} />
          Ajouter
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Début</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix/Gramme (DH)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {historiquePrixOr.map(item => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingId === item.id ? (
                    <input
                      type="date"
                      value={editForm.date_debut || ''}
                      onChange={(e) => setEditForm({...editForm, date_debut: e.target.value})}
                      className="px-2 py-1 border rounded"
                    />
                  ) : (
                    <span className="flex items-center gap-2">
                      <Calendar size={16} className="text-gray-400" />
                      {new Date(item.date_debut).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingId === item.id ? (
                    <input
                      type="text" inputMode="decimal" onFocus={e => e.target.select()}
                      step="0.01"
                      value={editForm.prix_gramme || ''}
                      onChange={(e) => setEditForm({...editForm, prix_gramme: e.target.value})}
                      className="w-32 px-2 py-1 border rounded"
                    />
                  ) : (
                    <span className="font-bold text-yellow-600">{item.prix_gramme} DH/g</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingId === item.id ? (
                    <div className="flex gap-2">
                      <button onClick={handleSave} className="text-green-600 hover:text-green-800">
                        <Save size={18} />
                      </button>
                      <button onClick={() => setEditingId(null)} className="text-red-600 hover:text-red-800">
                        <X size={18} />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800">
                      <Edit2 size={18} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Rendu du tableau Achats Matières
  const renderAchatsMatieres = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800">🛒 Achats Matières Premières</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} />
          Ajouter
        </button>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-gray-200">
        {achatsMatieres.map(item => (
          <div key={item.id} className="p-4 space-y-1">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-900">{item.type_matiere}</span>
              <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800">
                <Edit2 size={18} />
              </button>
            </div>
            <div className="text-sm text-gray-600">Dernier achat: {item.prix_dernier_achat} DH · Moy: {item.prix_moyen} DH</div>
            <div className="text-sm text-gray-500">Stock: {item.quantite_stock} {item.unite}</div>
          </div>
        ))}
      </div>

      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix Initial</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dernier Achat</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mai 2024</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mai 2026</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix Moyen</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {achatsMatieres.map(item => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  {item.type_matiere}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">{item.prix_achat_initial} DH</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                  {item.prix_dernier_achat} DH
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">{item.prix_achat_mai_2024} DH</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">{item.prix_achat_mai_2026} DH</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                  {item.prix_moyen} DH
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {item.quantite_stock} {item.unite}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800">
                    <Edit2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Rendu du tableau Achats Pierres
  const renderAchatsPierres = () => (
    <div className="space-y-6">
      {/* Statistiques */}
      {statistiquesPierres && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600">Total Achats</div>
            <div className="text-2xl font-bold text-blue-600">{statistiquesPierres.total_achats}</div>
            <div className="text-xs text-gray-500">{statistiquesPierres.montant_total?.toFixed(2)} DH</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600">Payé</div>
            <div className="text-2xl font-bold text-green-600">{statistiquesPierres.achats_payes}</div>
            <div className="text-xs text-gray-500">{statistiquesPierres.montant_paye_total?.toFixed(2)} DH</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600">Partiel</div>
            <div className="text-2xl font-bold text-orange-600">{statistiquesPierres.achats_partiels}</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600">Restant à Payer</div>
            <div className="text-2xl font-bold text-red-600">
              {statistiquesPierres.montant_restant_total?.toFixed(2)} DH
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">💎 Stock Pierres & Suivi Paiement</h2>
          <button
            onClick={() => setShowAddForm(true)}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={20} />
            Ajouter
          </button>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-gray-200">
          {achatsPierres.map(item => (
            <div key={item.id} className="p-4 space-y-1">
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-900">{item.reference}</span>
                <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800">
                  <Edit2 size={18} />
                </button>
              </div>
              <div className="text-sm text-gray-600">{item.type_pierre} · {item.poids_carats} ct</div>
              <div className="text-sm text-gray-600">Total: {item.prix_total} DH · Payé: <span className="text-green-600">{item.montant_paye} DH</span> · Restant: <span className="text-red-600">{item.montant_restant} DH</span></div>
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${item.statut_paiement === 'paye' ? 'bg-green-100 text-green-800' : item.statut_paiement === 'partiel' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'}`}>
                {item.statut_paiement === 'paye' ? 'Payé' : item.statut_paiement === 'partiel' ? 'Partiel' : 'Non payé'}
              </span>
            </div>
          ))}
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Poids</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payé</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Restant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attribué</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {achatsPierres.map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                    {item.reference}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{item.type_pierre}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{item.poids_carats} ct</td>
                  <td className="px-6 py-4 whitespace-nowrap font-medium">{item.prix_total} DH</td>
                  <td className="px-6 py-4 whitespace-nowrap text-green-600">{item.montant_paye} DH</td>
                  <td className="px-6 py-4 whitespace-nowrap text-red-600 font-medium">
                    {item.montant_restant} DH
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item.statut_paiement === 'paye' ? 'bg-green-100 text-green-800' :
                      item.statut_paiement === 'partiel' ? 'bg-orange-100 text-orange-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {item.statut_paiement === 'paye' ? 'Payé' :
                       item.statut_paiement === 'partiel' ? 'Partiel' : 'Non payé'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {item.attribue ? (
                      <Check size={18} className="text-green-600" />
                    ) : (
                      <X size={18} className="text-gray-400" />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800">
                      <Edit2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">💰 Gestion des Coûts</h1>

        {/* Onglets */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            <button
              onClick={() => setActiveTab('packaging')}
              className={`flex items-center gap-2 px-6 py-4 font-medium ${
                activeTab === 'packaging'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Package size={20} />
              Packaging
            </button>
            <button
              onClick={() => setActiveTab('couts-fabrication')}
              className={`flex items-center gap-2 px-6 py-4 font-medium ${
                activeTab === 'couts-fabrication'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Wrench size={20} />
              Coûts Fabrication
            </button>
            <button
              onClick={() => setActiveTab('prix-or')}
              className={`flex items-center gap-2 px-6 py-4 font-medium ${
                activeTab === 'prix-or'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <TrendingUp size={20} />
              Prix de l'Or
            </button>
            <button
              onClick={() => setActiveTab('matieres')}
              className={`flex items-center gap-2 px-6 py-4 font-medium ${
                activeTab === 'matieres'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <ShoppingCart size={20} />
              Matières Premières
            </button>
            <button
              onClick={() => setActiveTab('pierres')}
              className={`flex items-center gap-2 px-6 py-4 font-medium ${
                activeTab === 'pierres'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Gem size={20} />
              Pierres & Paiements
            </button>
          </div>
        </div>

        {/* Contenu */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        ) : (
          <>
            {activeTab === 'packaging' && renderPackaging()}
            {activeTab === 'couts-fabrication' && renderCoutsFabrication()}
            {activeTab === 'prix-or' && renderHistoriquePrixOr()}
            {activeTab === 'matieres' && renderAchatsMatieres()}
            {activeTab === 'pierres' && renderAchatsPierres()}
          </>
        )}
      </div>
    </div>
  );
};

export default GestionCouts;
